import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, getDocs, doc, updateDoc, query, where, orderBy } from 'firebase/firestore';
import { auth, db } from '../firebaseConfig.js';
import { onAuthStateChanged } from 'firebase/auth';
import {
  FaArrowLeft,
  FaSearch,
  FaFilter,
  FaEye,
  FaCheck,
  FaTimes,
  FaReply,
  FaVideo,
  FaFile,
  FaImage,
  FaFilePdf,
  FaQuestion,
  FaCalculator,
  FaBook,
  FaClock,
  FaUser,
  FaCalendarAlt,
  FaTag,
  FaSave,
  FaSpinner,
  FaLink,
  FaGraduationCap,
  FaExclamationTriangle,
  FaInfoCircle,
  FaEnvelope,
  FaEnvelopeOpen
} from 'react-icons/fa';
import './MateriaisAprovacao.css'; // Usando o mesmo CSS
import { normalizeText } from '../utils/text';

function PerguntasAlunos() {
  const navigate = useNavigate();
  
  // Estados principais
  const [perguntas, setPerguntas] = useState([]);
  const [perguntasFiltradas, setPerguntasFiltradas] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [currentUser, setCurrentUser] = useState(null);
  
  // Filtros
  const [filtroStatus, setFiltroStatus] = useState('nova');
  const [filtroTipo, setFiltroTipo] = useState('');
  const [filtroLida, setFiltroLida] = useState('');
  const [termoBusca, setTermoBusca] = useState('');
  
  // Modal de visualização/resposta
  const [perguntaSelecionada, setPerguntaSelecionada] = useState(null);
  const [modalAberto, setModalAberto] = useState(false);
  const [resposta, setResposta] = useState('');
  const [videoResposta, setVideoResposta] = useState('');
  const [salvandoResposta, setSalvandoResposta] = useState(false);
  const [mensagem, setMensagem] = useState('');

  // Autenticação
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, firebaseUser => {
      setCurrentUser(firebaseUser);
      if (firebaseUser) {
        // O usuário já é hidratado no App; evitar sobrescrever contexto aqui.
      } else {
        navigate('/admin-login');
      }
    });
    return () => unsubscribe();
  }, [navigate]);

  // Carregar perguntas
  useEffect(() => {
    if (currentUser) {
      carregarPerguntas();
    }
  }, [currentUser]);

  // Aplicar filtros
  useEffect(() => {
    aplicarFiltros();
  }, [perguntas, filtroStatus, filtroTipo, filtroLida, termoBusca]);

  const carregarPerguntas = async () => {
    setCarregando(true);
    try {
      const q = query(
        collection(db, 'perguntas_alunos'),
        orderBy('createdAt', 'desc')
      );
      const snapshot = await getDocs(q);
      
      const perguntasData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate ? doc.data().createdAt.toDate() : new Date(doc.data().createdAt),
        updatedAt: doc.data().updatedAt?.toDate ? doc.data().updatedAt.toDate() : new Date(doc.data().updatedAt)
      }));
      
      setPerguntas(perguntasData);
    } catch (error) {
      console.error('Erro ao carregar perguntas:', error);
      setMensagem('Erro ao carregar perguntas.');
    } finally {
      setCarregando(false);
    }
  };

  const aplicarFiltros = () => {
    let filtradas = perguntas;

    // Filtro por status
    if (filtroStatus) {
      filtradas = filtradas.filter(pergunta => pergunta.status === filtroStatus);
    }

    // Filtro por tipo
    if (filtroTipo) {
      filtradas = filtradas.filter(pergunta => 
        pergunta.tipo === filtroTipo || pergunta.tipoSolicitacao === filtroTipo
      );
    }

    // Filtro por lida/não lida
    if (filtroLida) {
      const isLida = filtroLida === 'lida';
      filtradas = filtradas.filter(pergunta => pergunta.lida === isLida);
    }

    // Filtro por termo de busca
    if (termoBusca) {
      const termo = normalizeText(termoBusca);
      filtradas = filtradas.filter(pergunta =>
        normalizeText(pergunta.titulo).includes(termo) ||
        normalizeText(pergunta.descricao).includes(termo) ||
        normalizeText(pergunta.authorName).includes(termo) ||
        normalizeText(pergunta.conteudo).includes(termo) ||
        normalizeText(pergunta.disciplina).includes(termo) ||
        normalizeText(pergunta.curso).includes(termo)
      );
    }

    setPerguntasFiltradas(filtradas);
  };

  const abrirModal = async (pergunta) => {
    setPerguntaSelecionada(pergunta);
    setResposta(pergunta.resposta || '');
    setVideoResposta(pergunta.videoResposta || '');
    setModalAberto(true);
    setMensagem('');

    // Marcar como lida se não estiver
    if (!pergunta.lida) {
      await marcarComoLida(pergunta.id);
    }
  };

  const fecharModal = () => {
    setModalAberto(false);
    setPerguntaSelecionada(null);
    setResposta('');
    setVideoResposta('');
    setMensagem('');
  };

  const marcarComoLida = async (perguntaId) => {
    try {
      await updateDoc(doc(db, 'perguntas_alunos', perguntaId), {
        lida: true,
        lidaEm: new Date(),
        lidaPor: currentUser.email
      });

      // Atualizar localmente
      setPerguntas(prev => prev.map(pergunta =>
        pergunta.id === perguntaId
          ? { ...pergunta, lida: true, lidaEm: new Date(), lidaPor: currentUser.email }
          : pergunta
      ));
    } catch (error) {
      console.error('Erro ao marcar como lida:', error);
    }
  };

  const alterarStatus = async (perguntaId, novoStatus) => {
    try {
      await updateDoc(doc(db, 'perguntas_alunos', perguntaId), {
        status: novoStatus,
        updatedAt: new Date(),
        reviewedBy: currentUser.email
      });

      // Atualizar localmente
      setPerguntas(prev => prev.map(pergunta =>
        pergunta.id === perguntaId
          ? { ...pergunta, status: novoStatus, reviewedBy: currentUser.email }
          : pergunta
      ));

      const statusText = novoStatus === 'respondida' ? 'respondida' : 
                        novoStatus === 'fechada' ? 'fechada' : novoStatus;
      setMensagem(`Pergunta marcada como ${statusText} com sucesso!`);
    } catch (error) {
      console.error('Erro ao alterar status:', error);
      setMensagem('Erro ao alterar status da pergunta.');
    }
  };

  const salvarResposta = async () => {
    if (!perguntaSelecionada) return;

    setSalvandoResposta(true);
    try {
      await updateDoc(doc(db, 'perguntas_alunos', perguntaSelecionada.id), {
        resposta: resposta,
        videoResposta: videoResposta,
        respondidoEm: new Date(),
        respondidoPor: currentUser.email,
        status: 'respondida', // Automaticamente marca como respondida
        lida: true, // Marca como lida também
        updatedAt: new Date()
      });

      // Atualizar localmente
      setPerguntas(prev => prev.map(pergunta =>
        pergunta.id === perguntaSelecionada.id
          ? {
              ...pergunta,
              resposta,
              videoResposta,
              respondidoEm: new Date(),
              respondidoPor: currentUser.email,
              status: 'respondida',
              lida: true
            }
          : pergunta
      ));

      setMensagem('Resposta salva e pergunta marcada como respondida com sucesso!');
      setTimeout(() => {
        fecharModal();
      }, 2000);
    } catch (error) {
      console.error('Erro ao salvar resposta:', error);
      setMensagem('Erro ao salvar resposta.');
    } finally {
      setSalvandoResposta(false);
    }
  };

  const getTipoIcon = (tipo, tipoSolicitacao) => {
    // Priorizar tipoSolicitacao se disponível
    if (tipoSolicitacao) {
      switch (tipoSolicitacao) {
        case 'duvida': return <FaQuestion />;
        case 'exercicio': return <FaCalculator />;
        case 'prova': return <FaGraduationCap />;
        case 'material': return <FaBook />;
        case 'outro': return <FaInfoCircle />;
        default: return <FaQuestion />;
      }
    }
    
    switch (tipo) {
      case 'duvida': return <FaQuestion />;
      case 'exercicio': return <FaCalculator />;
      case 'material': return <FaBook />;
      default: return <FaQuestion />;
    }
  };

  const getMaterialIcon = (tipoMaterial, materialType) => {
    if (tipoMaterial === 'video') return <FaVideo />;
    if (tipoMaterial === 'arquivo') {
      if (materialType === 'PDF') return <FaFilePdf />;
      if (['JPG', 'JPEG', 'PNG'].includes(materialType)) return <FaImage />;
      return <FaFile />;
    }
    return <FaFile />;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'respondida': return '#16a34a';
      case 'fechada': return '#6b7280';
      case 'nova': return '#3b82f6';
      default: return '#6b7280';
    }
  };

  const getVideoThumbnail = (videoUrl) => {
    if (!videoUrl) return null;
    
    if (videoUrl.includes('youtube.com') || videoUrl.includes('youtu.be')) {
      const videoId = videoUrl.match(/(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/);
      return videoId ? `https://img.youtube.com/vi/${videoId[1]}/hqdefault.jpg` : null;
    }
    
    if (videoUrl.includes('vimeo.com')) {
      const videoId = videoUrl.match(/(?:vimeo\.com\/)([0-9]+)/);
      return videoId ? `https://vumbnail.com/${videoId[1]}.jpg` : null;
    }
    
    return null;
  };

  const formatTipoSolicitacao = (tipoSolicitacao) => {
    const tipos = {
      'duvida': 'Dúvida sobre conteúdo',
      'exercicio': 'Resolução de exercício',
      'prova': 'Prova resolvida',
      'material': 'Material complementar',
      'outro': 'Outro'
    };
    return tipos[tipoSolicitacao] || tipoSolicitacao;
  };

  const formatUrgencia = (urgencia) => {
    const urgencias = {
      'normal': 'Normal (até 7 dias)',
      'urgente': 'Urgente (até 3 dias)',
      'muito_urgente': 'Muito urgente (até 24h)'
    };
    return urgencias[urgencia] || urgencia;
  };

  const formatFormatoResposta = (formatos) => {
    if (!formatos || !Array.isArray(formatos) || formatos.length === 0) return null;
    
    const labels = {
      'videoaula': 'Videoaula',
      'material_escrito': 'Material escrito',
      'resumo': 'Resumo',
      'exercicio_resolvido': 'Exercício resolvido'
    };
    
    return formatos.map(formato => labels[formato] || formato).join(', ');
  };

  return (
    <div className="materiais-aprovacao-container">
      {/* Header */}
      <header className="admin-header">
        <button className="back-button" onClick={() => navigate('/admin-panel')}>
          <FaArrowLeft /> Voltar ao Painel
        </button>
        <div className="header-title">
          <h1>Perguntas dos Alunos</h1>
          <p>Gerencie dúvidas e questões enviadas pelos alunos</p>
        </div>
      </header>

      {/* Filtros */}
      <div className="filtros-container">
        <div className="filtros-linha">
          <div className="filtro-grupo">
            <label>Status:</label>
            <select
              value={filtroStatus}
              onChange={(e) => setFiltroStatus(e.target.value)}
            >
              <option value="">Todos</option>
              <option value="nova">Nova</option>
              <option value="respondida">Respondida</option>
              <option value="fechada">Fechada</option>
            </select>
          </div>

          <div className="filtro-grupo">
            <label>Tipo:</label>
            <select
              value={filtroTipo}
              onChange={(e) => setFiltroTipo(e.target.value)}
            >
              <option value="">Todos</option>
              <option value="duvida">Dúvida</option>
              <option value="exercicio">Exercício</option>
              <option value="prova">Prova</option>
            </select>
          </div>

          <div className="filtro-grupo">
            <label>Leitura:</label>
            <select
              value={filtroLida}
              onChange={(e) => setFiltroLida(e.target.value)}
            >
              <option value="">Todas</option>
              <option value="nao_lida">Não lidas</option>
              <option value="lida">Lidas</option>
            </select>
          </div>

          <div className="filtro-busca">
            <FaSearch className="busca-icon" />
            <input
              type="text"
              placeholder="Buscar por título, autor ou conteúdo..."
              value={termoBusca}
              onChange={(e) => setTermoBusca(e.target.value)}
            />
          </div>
        </div>
      </div>

      {/* Lista de Perguntas */}
      <div className="materiais-lista-container">
        {carregando ? (
          <div className="loading-container">
            <FaSpinner className="loading-spinner" />
            <p>Carregando perguntas...</p>
          </div>
        ) : perguntasFiltradas.length === 0 ? (
          <div className="empty-state">
            <FaQuestion className="empty-icon" />
            <h3>Nenhuma pergunta encontrada</h3>
            <p>Não há perguntas com os filtros selecionados.</p>
          </div>
        ) : (
          <div className="materiais-grid">
            {perguntasFiltradas.map(pergunta => (
              <div key={pergunta.id} className={`material-card ${!pergunta.lida ? 'has-notification' : ''}`}>
                <div className="material-header">
                  <div className="material-tipo">
                    {getTipoIcon(pergunta.tipo, pergunta.tipoSolicitacao)}
                    <span>{pergunta.tipoSolicitacao ? formatTipoSolicitacao(pergunta.tipoSolicitacao) : pergunta.tipo}</span>
                  </div>
                  <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                    {!pergunta.lida && (
                      <div style={{ 
                        backgroundColor: '#dc2626', 
                        color: 'white',
                        fontSize: '0.7rem',
                        padding: '0.25rem 0.5rem',
                        borderRadius: '12px',
                        fontWeight: 'bold'
                      }}>
                        <FaEnvelope style={{ marginRight: '0.25rem' }} />
                        NÃO LIDA
                      </div>
                    )}
                    <div 
                      className="material-status" 
                      style={{ 
                        backgroundColor: getStatusColor(pergunta.status),
                        color: 'white'
                      }}
                    >
                      {pergunta.status === 'nova' ? 'NOVA' : 
                       pergunta.status === 'respondida' ? 'RESPONDIDA' :
                       pergunta.status === 'fechada' ? 'FECHADA' :
                       pergunta.status?.toUpperCase()}
                    </div>
                  </div>
                </div>

                <div className="material-content">
                  <h3 className="material-titulo">{pergunta.titulo}</h3>
                  <p className="material-descricao">{pergunta.descricao}</p>
                  
                  <div className="material-meta">
                    <div className="meta-item">
                      <FaUser />
                      <span>{pergunta.authorName}</span>
                    </div>
                    <div className="meta-item">
                      <FaCalendarAlt />
                      <span>{pergunta.createdAt.toLocaleDateString('pt-BR')}</span>
                    </div>
                    {pergunta.disciplina && (
                      <div className="meta-item">
                        <FaGraduationCap />
                        <span>{pergunta.disciplina}</span>
                      </div>
                    )}
                    {pergunta.categoria && (
                      <div className="meta-item">
                        <FaTag />
                        <span>{pergunta.categoria}</span>
                      </div>
                    )}
                    {pergunta.urgencia && pergunta.urgencia !== 'normal' && (
                      <div className="meta-item">
                        <FaExclamationTriangle />
                        <span style={{ color: '#dc2626', fontWeight: 'bold' }}>
                          {formatUrgencia(pergunta.urgencia)}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Prévia do material */}
                  {pergunta.tipoMaterial === 'video' && pergunta.videoUrl && (
                    <div className="material-preview">
                      <img 
                        src={getVideoThumbnail(pergunta.videoUrl)}
                        alt="Thumbnail do vídeo"
                        className="video-thumbnail-small"
                        onError={(e) => e.target.style.display = 'none'}
                      />
                    </div>
                  )}

                  {pergunta.tipoMaterial === 'arquivo' && pergunta.arquivoURL && (
                    <div className="material-preview">
                      <div className="arquivo-info">
                        {getMaterialIcon(pergunta.tipoMaterial, pergunta.materialType)}
                        <span>Arquivo anexado</span>
                      </div>
                    </div>
                  )}
                </div>

                <div className="material-actions">
                  <button
                    onClick={() => abrirModal(pergunta)}
                    className="btn-visualizar"
                  >
                    <FaEye /> Ver Detalhes
                  </button>
                  
                  {pergunta.status === 'nova' && (
                    <>
                      <button
                        onClick={() => alterarStatus(pergunta.id, 'respondida')}
                        className="btn-aprovar"
                      >
                        <FaCheck /> Marcar Respondida
                      </button>
                      <button
                        onClick={() => alterarStatus(pergunta.id, 'fechada')}
                        className="btn-rejeitar"
                      >
                        <FaTimes /> Fechar
                      </button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Modal de Visualização/Resposta */}
      {modalAberto && perguntaSelecionada && (
        <div className="modal-overlay" onClick={fecharModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{perguntaSelecionada.titulo}</h2>
              <button onClick={fecharModal} className="modal-close">
                <FaTimes />
              </button>
            </div>

            <div className="modal-body">
              {/* Informações da Pergunta */}
              <div className="material-info-section">
                <div className="info-grid">
                  <div className="info-item">
                    <label>Autor:</label>
                    <span>{perguntaSelecionada.authorName} ({perguntaSelecionada.authorEmail})</span>
                  </div>
                  <div className="info-item">
                    <label>Tipo:</label>
                    <span>{perguntaSelecionada.tipoSolicitacao ? formatTipoSolicitacao(perguntaSelecionada.tipoSolicitacao) : perguntaSelecionada.tipo}</span>
                  </div>
                  <div className="info-item">
                    <label>Data de Envio:</label>
                    <span>{perguntaSelecionada.createdAt.toLocaleDateString('pt-BR')}</span>
                  </div>
                  <div className="info-item">
                    <label>Status:</label>
                    <span style={{ color: getStatusColor(perguntaSelecionada.status) }}>
                      {perguntaSelecionada.status?.toUpperCase()}
                    </span>
                  </div>
                  <div className="info-item">
                    <label>Lida:</label>
                    <span style={{ color: perguntaSelecionada.lida ? '#16a34a' : '#dc2626' }}>
                      {perguntaSelecionada.lida ? (
                        <>
                          <FaEnvelopeOpen style={{ marginRight: '0.25rem' }} />
                          SIM
                        </>
                      ) : (
                        <>
                          <FaEnvelope style={{ marginRight: '0.25rem' }} />
                          NÃO
                        </>
                      )}
                    </span>
                  </div>
                  {perguntaSelecionada.disciplina && (
                    <div className="info-item">
                      <label>Disciplina:</label>
                      <span>{perguntaSelecionada.disciplina}</span>
                    </div>
                  )}
                  {perguntaSelecionada.curso && (
                    <div className="info-item">
                      <label>Curso:</label>
                      <span>{perguntaSelecionada.curso}</span>
                    </div>
                  )}
                  {perguntaSelecionada.semestre && (
                    <div className="info-item">
                      <label>Semestre:</label>
                      <span>{perguntaSelecionada.semestre}º Semestre</span>
                    </div>
                  )}
                  {perguntaSelecionada.urgencia && (
                    <div className="info-item">
                      <label>Urgência:</label>
                      <span style={{ 
                        color: perguntaSelecionada.urgencia === 'muito_urgente' ? '#dc2626' : 
                               perguntaSelecionada.urgencia === 'urgente' ? '#f59e0b' : '#6b7280',
                        fontWeight: perguntaSelecionada.urgencia !== 'normal' ? 'bold' : 'normal'
                      }}>
                        {formatUrgencia(perguntaSelecionada.urgencia)}
                      </span>
                    </div>
                  )}
                  {perguntaSelecionada.categoria && (
                    <div className="info-item">
                      <label>Categoria:</label>
                      <span>{perguntaSelecionada.categoria}</span>
                    </div>
                  )}
                  {perguntaSelecionada.subcategoria && (
                    <div className="info-item">
                      <label>Subcategoria:</label>
                      <span>{perguntaSelecionada.subcategoria}</span>
                    </div>
                  )}
                </div>

                {/* Preferências de formato de resposta */}
                {perguntaSelecionada.formatoResposta && perguntaSelecionada.formatoResposta.length > 0 && (
                  <div className="descricao-section">
                    <label>Formato de resposta preferido:</label>
                    <p style={{ color: '#209cd3', fontWeight: '500' }}>
                      {formatFormatoResposta(perguntaSelecionada.formatoResposta)}
                    </p>
                  </div>
                )}

                <div className="descricao-section">
                  <label>Descrição:</label>
                  <p>{perguntaSelecionada.descricao}</p>
                </div>

                {perguntaSelecionada.conteudo && (
                  <div className="conteudo-section">
                    <label>Conteúdo/Pergunta:</label>
                    <div className="conteudo-box">
                      {perguntaSelecionada.conteudo}
                    </div>
                  </div>
                )}

                {/* Link Relacionado */}
                {perguntaSelecionada.linkRelacionado && (
                  <div className="anexo-section">
                    <label>Link Relacionado:</label>
                    <a 
                      href={perguntaSelecionada.linkRelacionado}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="video-link"
                      style={{ display: 'inline-flex', marginTop: '0.5rem' }}
                    >
                      <FaLink /> {perguntaSelecionada.linkRelacionado}
                    </a>
                  </div>
                )}

                {/* Material Anexado */}
                {perguntaSelecionada.tipoMaterial === 'video' && perguntaSelecionada.videoUrl && (
                  <div className="anexo-section">
                    <label>Vídeo Anexado:</label>
                    <div className="video-preview">
                      <img 
                        src={getVideoThumbnail(perguntaSelecionada.videoUrl)}
                        alt="Thumbnail do vídeo"
                        className="video-thumbnail"
                        onClick={() => window.open(perguntaSelecionada.videoUrl, '_blank')}
                        onError={(e) => e.target.style.display = 'none'}
                      />
                      <a 
                        href={perguntaSelecionada.videoUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="video-link"
                      >
                        <FaVideo /> Assistir Vídeo
                      </a>
                    </div>
                  </div>
                )}

                {perguntaSelecionada.tipoMaterial === 'arquivo' && perguntaSelecionada.arquivoURL && (
                  <div className="anexo-section">
                    <label>Arquivo Anexado:</label>
                    <a 
                      href={perguntaSelecionada.arquivoURL}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="arquivo-link"
                    >
                      {getMaterialIcon(perguntaSelecionada.tipoMaterial, perguntaSelecionada.materialType)}
                      Ver Arquivo
                    </a>
                  </div>
                )}

                {/* Arquivo URL direto (se não for tipoMaterial arquivo mas existir arquivoURL) */}
                {perguntaSelecionada.tipoMaterial !== 'arquivo' && perguntaSelecionada.arquivoURL && (
                  <div className="anexo-section">
                    <label>Arquivo Anexado:</label>
                    <a 
                      href={perguntaSelecionada.arquivoURL}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="arquivo-link"
                    >
                      {getMaterialIcon('arquivo', perguntaSelecionada.materialType)}
                      Ver Arquivo
                    </a>
                  </div>
                )}

                {/* Material URL (campo legado) */}
                {perguntaSelecionada.materialUrl && perguntaSelecionada.materialUrl !== perguntaSelecionada.arquivoURL && (
                  <div className="anexo-section">
                    <label>Material URL:</label>
                    <a 
                      href={perguntaSelecionada.materialUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="arquivo-link"
                    >
                      {getMaterialIcon('arquivo', perguntaSelecionada.materialType)}
                      Ver Material
                    </a>
                  </div>
                )}
              </div>

              {/* Seção de Resposta */}
              <div className="resposta-section">
                <h3>
                  <FaReply /> Resposta do Professor
                </h3>

                <div className="resposta-form">
                  <div className="form-group">
                    <label>Resposta em Texto:</label>
                    <textarea
                      value={resposta}
                      onChange={(e) => setResposta(e.target.value)}
                      placeholder="Digite sua resposta aqui..."
                      rows={6}
                    />
                  </div>

                  <div className="form-group">
                    <label>Vídeo Resposta (YouTube/Vimeo) - Opcional:</label>
                    <input
                      type="url"
                      value={videoResposta}
                      onChange={(e) => setVideoResposta(e.target.value)}
                      placeholder="https://www.youtube.com/watch?v=... ou https://vimeo.com/..."
                    />
                    {videoResposta && getVideoThumbnail(videoResposta) && (
                      <div className="video-preview-small">
                        <img 
                          src={getVideoThumbnail(videoResposta)}
                          alt="Prévia da resposta em vídeo"
                          className="video-thumbnail-small"
                          onError={(e) => e.target.style.display = 'none'}
                        />
                        <p>Prévia da resposta em vídeo</p>
                      </div>
                    )}
                  </div>

                  {mensagem && (
                    <div className={`mensagem ${mensagem.includes('Erro') ? 'erro' : 'sucesso'}`}>
                      {mensagem}
                    </div>
                  )}

                  <div className="resposta-actions">
                    <button
                      onClick={salvarResposta}
                      disabled={salvandoResposta || !resposta.trim()}
                      className="btn-salvar-resposta"
                    >
                      {salvandoResposta ? (
                        <>
                          <FaSpinner className="spinner" /> Salvando...
                        </>
                      ) : (
                        <>
                          <FaSave /> Salvar Resposta e Marcar como Respondida
                        </>
                      )}
                    </button>

                    {perguntaSelecionada.status === 'nova' && (
                      <div className="status-actions">
                        <button
                          onClick={() => {
                            alterarStatus(perguntaSelecionada.id, 'respondida');
                            fecharModal();
                          }}
                          className="btn-aprovar"
                        >
                          <FaCheck /> Apenas Marcar como Respondida
                        </button>
                        <button
                          onClick={() => {
                            alterarStatus(perguntaSelecionada.id, 'fechada');
                            fecharModal();
                          }}
                          className="btn-rejeitar"
                        >
                          <FaTimes /> Fechar Pergunta
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                {/* Resposta Existente */}
                {perguntaSelecionada.resposta && (
                  <div className="resposta-existente">
                    <h4>Resposta Atual:</h4>
                    <div className="resposta-conteudo">
                      <p>{perguntaSelecionada.resposta}</p>
                      {perguntaSelecionada.videoResposta && (
                        <div className="video-resposta">
                          <img 
                            src={getVideoThumbnail(perguntaSelecionada.videoResposta)}
                            alt="Resposta em vídeo"
                            className="video-thumbnail-small"
                            onClick={() => window.open(perguntaSelecionada.videoResposta, '_blank')}
                            onError={(e) => e.target.style.display = 'none'}
                          />
                          <a 
                            href={perguntaSelecionada.videoResposta}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <FaVideo /> Ver Resposta em Vídeo
                          </a>
                        </div>
                      )}
                      {perguntaSelecionada.respondidoPor && (
                        <p className="resposta-meta">
                          Respondido por: {perguntaSelecionada.respondidoPor} em {' '}
                          {perguntaSelecionada.respondidoEm?.toLocaleDateString('pt-BR')}
                        </p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default PerguntasAlunos;
